import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import { toast } from 'sonner'
import { Copy, Download, FileText, Sparkles } from 'lucide-react'
import { api, type Brief } from '@/lib/api'
import { cn, formatRelative } from '@/lib/utils'

type Kind = 'content' | 'strategy'

const PRESETS: Record<Kind, { title: string; audience: string; keywords: string }> = {
  content: {
    title: 'Spring turkey opener: scouting week by week',
    audience: 'Michigan hunters, intermediate',
    keywords: 'turkey scouting, michigan dnr, spring gobbler tactics',
  },
  strategy: {
    title: 'Own the plains-game planning pillar for North American hunters',
    audience: 'Hunters considering a first African safari',
    keywords: 'plains game, africa safari cost, outfitter vetting',
  },
}

export function BriefsPage() {
  const qc = useQueryClient()
  const [kind, setKind] = useState<Kind>('content')
  const [title, setTitle] = useState(PRESETS.content.title)
  const [audience, setAudience] = useState(PRESETS.content.audience)
  const [keywords, setKeywords] = useState(PRESETS.content.keywords)
  const [activeBrief, setActiveBrief] = useState<Brief | null>(null)

  const { data: briefs = [] } = useQuery({ queryKey: ['briefs'], queryFn: api.briefs })

  const gen = useMutation({
    mutationFn: () =>
      api.generateBrief({
        kind,
        title,
        audience,
        keywords: keywords
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean),
      }),
    onSuccess: ({ brief }) => {
      setActiveBrief(brief)
      qc.invalidateQueries({ queryKey: ['briefs'] })
      toast.success(`${kind === 'strategy' ? 'Strategy' : 'Content'} brief ready`)
    },
    onError: (err) => toast.error('Generation failed', { description: String(err) }),
  })

  function switchKind(next: Kind) {
    setKind(next)
    setTitle(PRESETS[next].title)
    setAudience(PRESETS[next].audience)
    setKeywords(PRESETS[next].keywords)
    setActiveBrief(null)
  }

  function copy(text: string) {
    navigator.clipboard.writeText(text)
    toast.success('Copied markdown')
  }
  function download(b: Brief) {
    const blob = new Blob([b.body_md], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${b.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 60)}.md`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-3 md:p-5 max-w-[1400px] mx-auto flex flex-col gap-3.5">
      <header>
        <h1 className="text-xl font-medium">Briefs</h1>
        <p className="text-xs text-ink-secondary mt-0.5">
          Hand a topic to Claude and get a brief. Content briefs are for writers; strategy briefs come from the
          Strategist agent's perspective.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-3.5 min-w-0">
        <section className="border border-border rounded-md bg-surface-card p-4 flex flex-col gap-3 h-fit">
          <div className="grid grid-cols-2 gap-1 p-1 bg-surface-muted rounded-md">
            {(['content', 'strategy'] as const).map((k) => (
              <button
                key={k}
                onClick={() => switchKind(k)}
                className={cn(
                  'text-xs px-2 py-1.5 rounded capitalize transition-colors flex items-center justify-center gap-1.5',
                  kind === k ? 'bg-surface-card shadow-sm font-medium' : 'text-ink-secondary hover:text-ink-primary',
                )}
              >
                {k === 'strategy' ? <Sparkles size={12} /> : <FileText size={12} />}
                {k} brief
              </button>
            ))}
          </div>

          <label className="text-xs text-ink-secondary">
            Title
            <input
              className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>

          <label className="text-xs text-ink-secondary">
            Audience
            <input
              className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
            />
          </label>

          <label className="text-xs text-ink-secondary">
            Target keywords (comma-separated)
            <input
              className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
            />
          </label>

          <button
            onClick={() => gen.mutate()}
            disabled={gen.isPending || !title.trim()}
            className="mt-1 bg-brand-600 text-white rounded-md px-3 py-2 text-sm hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-1.5"
          >
            <Sparkles size={14} />
            {gen.isPending ? 'Generating…' : `Generate ${kind} brief`}
          </button>
          <p className="text-[11px] text-ink-tertiary">
            Calls Claude (claude-sonnet-4-6) and stores the output in D1. ~3–6 s.
          </p>

          <div className="border-t border-border pt-3 mt-1">
            <div className="text-xs font-medium text-ink-secondary mb-1.5">Recent briefs</div>
            {briefs.length === 0 ? (
              <p className="text-[11px] text-ink-tertiary italic">Nothing yet. Generate one above.</p>
            ) : (
              <div className="flex flex-col gap-1.5 max-h-[320px] overflow-auto">
                {briefs.slice(0, 20).map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setActiveBrief(b)}
                    className={cn(
                      'text-left px-2 py-1.5 rounded border text-xs transition-colors',
                      activeBrief?.id === b.id
                        ? 'border-brand-400 bg-brand-50'
                        : 'border-border hover:bg-surface-muted',
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      {b.kind === 'strategy' ? (
                        <Sparkles size={11} className="text-brand-600" />
                      ) : (
                        <FileText size={11} className="text-ink-tertiary" />
                      )}
                      <span className="font-medium truncate flex-1">{b.title}</span>
                    </div>
                    <div className="text-ink-tertiary text-[10px] mt-0.5">
                      {b.kind} · {formatRelative(b.created_at)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="border border-border rounded-md bg-surface-card min-w-0">
          {activeBrief || gen.isPending ? (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-ink-tertiary">
                    {activeBrief?.kind ?? kind} brief
                  </div>
                  <div className="text-sm font-medium truncate">
                    {activeBrief?.title ?? title}
                  </div>
                </div>
                {activeBrief && (
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => copy(activeBrief.body_md)}
                      className="text-xs border border-border rounded-md px-2 py-1 hover:bg-surface-muted flex items-center gap-1"
                    >
                      <Copy size={12} /> Copy
                    </button>
                    <button
                      onClick={() => download(activeBrief)}
                      className="text-xs border border-border rounded-md px-2 py-1 hover:bg-surface-muted flex items-center gap-1"
                    >
                      <Download size={12} /> .md
                    </button>
                  </div>
                )}
              </div>
              <div className="p-6 overflow-auto">
                {gen.isPending ? (
                  <div className="text-sm text-ink-tertiary flex items-center gap-2">
                    <Sparkles className="animate-pulse" size={14} />
                    Claude is writing the brief…
                  </div>
                ) : (
                  activeBrief && (
                    <article className="max-w-none text-sm leading-relaxed text-ink-primary [&_h1]:text-lg [&_h1]:font-medium [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-base [&_h2]:font-medium [&_h2]:mt-4 [&_h2]:mb-1.5 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_li]:mb-0.5 [&_strong]:font-medium [&_strong]:text-ink-primary [&_code]:bg-surface-muted [&_code]:px-1 [&_code]:rounded [&_code]:text-[12px]">
                      <ReactMarkdown>{activeBrief.body_md}</ReactMarkdown>
                    </article>
                  )
                )}
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[400px] flex items-center justify-center text-sm text-ink-tertiary text-center px-6">
              Generate a brief, or pick a recent one from the sidebar.
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
